import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useRef } from 'react';
import useRefCallback from '@hzdg/use-ref-callback';
import { getScrollRect, useNearestScrollNodeRef, useScrollEffect } from './utils';
/**
 * `ScrollPosition` is an object of `top` and `left` values,
 * where `top` is the number of pixels the nearest scrollable container
 * is scrolled vertically, and `left` is the number of pixels the
 * nearest scrollable container is scrolled horizontally.
 */

/**
 * `getScrollPosition` returns a `ScrollPosition` for a given `Event` target.
 *
 * `ScrollPosition` will be an object of `top` and `left` values,
 * where `top` is the number of pixels the nearest scrollable container
 * is scrolled vertically, and `left` is the number of pixels the
 * nearest scrollable container is scrolled horizontally.
 */
export function getScrollPosition(event) {
  var target = event.currentTarget;
  if (target instanceof HTMLElement || target instanceof Document) {
    var rect = getScrollRect(target);
    return {
      top: rect.top,
      left: rect.left
    };
  }
  return {
    top: null,
    left: null
  };
}
/**
 * `useScrollPosition` is a React hook for components
 * that care about the nearest scrollable container's scroll position.
 *
 * Expects a `handler` that will receive a `ScrollPosition` each time the
 * nearest scrollable element's scroll position changes.
 *
 * If a `providedRef` is passed to `useScrollPosition`,
 * returns `undefined`.
 *
 * If no `providedRef` is passed, returns a callback `ref`.
 * The `ref` should be passed to an underlying DOM node.
 * Note that the node does not have to be scrollable itself,
 * as `useScrollPosition` will traverse the DOM to find a scrollable parent
 * to observe.
 */

function useScrollPosition(providedRefOrHandler, handler) {
  var providedRef = undefined;
  if ('current' in providedRefOrHandler) {
    providedRef = providedRefOrHandler;
  } else {
    handler = providedRefOrHandler;
  } // Keep a ref to the nearest scrollable container.

  var _useRefCallback = useRefCallback(null),
    _useRefCallback2 = _slicedToArray(_useRefCallback, 2),
    ref = _useRefCallback2[0],
    setRef = _useRefCallback2[1];
  if (providedRef) setRef(providedRef.current);
  var scrollRef = useNearestScrollNodeRef(ref);
  var changeHandler = useRef(handler);
  changeHandler.current = handler; // Subscribe to scroll events on the nearest scrolling element,
  // calling the handler whenever a scroll event occurs.

  useScrollEffect(scrollRef,
  /**
   * `handleEvent` will update the current change handler
   * with a new `ScrollPosition` whenever the nearest
   * scrollable container's scroll position changes.
   */
  function handleEvent(event) {
    var cb = changeHandler.current;
    if (typeof cb === 'function') {
      var _position = getScrollPosition(event);
      cb(_position);
    }
  }, []); // If a ref has been provided, return nothing.
  // If a ref has not not been provided, return a callback ref.

  if (!providedRef) return setRef;
}
export default useScrollPosition;